import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { validateBr } from 'js-brasil';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { TextField } from '@mui/material';
import { brazilianStates, countries } from '../../utils/constants';

import CompanyStep1Data from '../../pages/superAdmin/components/steps/CompanyStep1Data';
import CompanyStep2Address from '../../pages/superAdmin/components/steps/CompanyStep2Address';
import CompanyStep3Settings from '../../pages/superAdmin/components/steps/CompanyStep3Settings';

const PhoneInputAdapter = React.forwardRef(function PhoneInputAdapter(props, ref) {
  return <TextField {...props} inputRef={ref} fullWidth variant="outlined" />;
});

export default function CompanyEditModal({ company, onClose, onSave, currentUser }) {
  const [formData, setFormData] = useState({
    // Campos alinhados com o schema.prisma
    name: '', // era corporateName
    slug: '', // era tradeName/userName
    type: 'EAD', // Novo campo para o tipo da empresa, default 'EAD'
    cnpjOrTaxId: '', // era cnpj
    stateRegistration: '', // Campo não presente no schema, mantido por consistência da UI
    description: '', // era businessArea
    address: '', // era street
    number: '',
    complement: '',
    district: '', // Campo não presente no schema, mantido por consistência da UI
    city: '',
    state: '',
    postalCode: '', // era cep
    country: 'Brasil',
    phone: '',
    emailContact: '', // era contactEmail
    adminIds: [],
    subscribedPlanId: '',
    primaryColor: '#1976D2', // Cor padrão MUI
    secondaryColor: '#DC004E', // Cor padrão MUI
    logo: null,
    trainersLimit: 10, // era instructorsLimit
    studentsLimit: 100,
    modulesLimit: 20,
  });
  const [plans, setPlans] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(1);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [adminInputValue, setAdminInputValue] = useState('');
  const [newAdminName, setNewAdminName] = useState('');

  const isCreating = !company?.id;

  // Função para construir a URL completa da logo a partir do nome do arquivo
  const buildLogoUrl = logoFileName => {
    if (!logoFileName) return null;

    // Se já é uma URL completa, retorna direto
    if (logoFileName.startsWith('http://') || logoFileName.startsWith('https://')) {
      return logoFileName;
    }

    // Se contém barra, é um caminho relativo antigo
    if (logoFileName.includes('/')) {
      const apiBase = process.env.REACT_APP_URL_BASE || '';
      const apiOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/$/, '');
      return `${apiOrigin}${logoFileName}`;
    }

    // É apenas o nome do arquivo - constrói a URL
    const apiBase = process.env.REACT_APP_URL_BASE || '';
    const apiOrigin = apiBase.replace(/\/api\/?$/i, '').replace(/\/$/, '');
    return `${apiOrigin}/uploads/${logoFileName}`;
  };

  useEffect(() => {
    if (company) {
      // Se estiver editando, preenche o formulário com os dados da empresa.
      // Usar || '' para evitar que valores nulos se tornem "uncontrolled inputs".
      setFormData({
        name: company.name || '',
        slug: company.slug || '',
        type: company.type || 'EAD',
        cnpjOrTaxId: company.cnpjOrTaxId || '',
        stateRegistration: company.stateRegistration || '',
        description: company.description || '',
        address: company.address || '',
        number: company.number || '',
        complement: company.complement || '',
        district: company.district || '',
        city: company.city || '',
        state: company.state || '',
        postalCode: company.postalCode || '',
        country: company.country || 'Brasil',
        phone: company.phone || '',
        emailContact: company.emailContact || '',
        adminIds: company.admins?.map(a => a.id) || [],
        subscribedPlanId: company.subscribedPlanId || '',
        primaryColor: company.primaryColor || '#1976D2',
        secondaryColor: company.secondaryColor || '#DC004E',
        logo: null, // logo é um arquivo, não vem da API
        logoUrl: company.logoUrl || null,
        trainersLimit: company.trainersLimit ?? 10,
        studentsLimit: company.studentsLimit ?? 100,
        modulesLimit: company.modulesLimit ?? 20,
      });
    } else {
      // Se estiver criando, configura o tipo padrão com base no usuário atual.
      const defaultType = currentUser?.role === 'ADMIN_EAD' ? 'CLIENT' : 'EAD';
      // Reseta para o estado inicial, mas define o tipo.
      setFormData(prev => ({
        ...prev, // Mantém o estado inicial do useState
        type: defaultType,
        country: 'Brasil',
        primaryColor: '#1976D2',
        secondaryColor: '#DC004E',
        trainersLimit: 10,
        studentsLimit: 100,
        modulesLimit: 20,
      }));
    }

    const fetchPrerequisites = async () => {
      try {
        setLoading(true);
        const [usersRes, plansRes] = await Promise.all([
          api.get('/users'),
          api.get('/plans'), // Busca os planos da API
        ]);
        setPlans(plansRes.data);
        // Filtra usuários que podem ser admins
        setAdmins(usersRes.data.filter(u => u.role === 'ADMIN_EAD'));
      } catch (error) {
        console.error('Erro ao carregar dados para o modal', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPrerequisites();
  }, [company, currentUser]);

  useEffect(() => {
    const fetchCities = async () => {
      if (formData.state) {
        setLoadingCities(true);
        setCities([]); // Limpa cidades anteriores
        try {
          const response = await api.get(
            `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${formData.state}/municipios`
          );
          const data = response.data;
          // Ordena as cidades por nome
          const sortedCities = data.sort((a, b) => a.nome.localeCompare(b.nome));
          setCities(sortedCities);
        } catch (error) {
          console.error('Erro ao buscar cidades:', error);
          // Opcional: setar um erro específico para cidades
        } finally {
          setLoadingCities(false);
        }
      } else {
        setCities([]); // Limpa se nenhum estado for selecionado
      }
    };
    fetchCities();
  }, [formData.state]);

  const handleUsernameBlur = async e => {
    const slug = e.target.value;
    if (!slug) {
      // Limpa o erro se o campo estiver vazio
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.slug;
        return newErrors;
      });
      return;
    }

    // Se estiver editando uma empresa e o slug não foi alterado, não há erro de duplicidade.
    // Evita chamar a API desnecessariamente e evita erro de "slug já em uso" para o próprio slug.
    if (!isCreating && company.slug === slug) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.slug; // Limpa qualquer erro de slug anterior
        return newErrors;
      });
      return; // Sai da função, pois o slug é válido para a edição
    }

    try {
      // A verificação de slug não precisa do ID da empresa, pois o slug deve ser globalmente único.
      await api.get(`/companies/check-slug/${slug}`);

      // Se a requisição for bem-sucedida (status 200), o slug está disponível.
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.slug;
        return newErrors;
      });
    } catch (error) {
      // Axios joga um erro para status 4xx/5xx. Precisamos verificar se é o 409 que esperamos.
      if (error.response && error.response.status === 409) {
        // O backend retorna 409 quando o slug já existe.
        setErrors(prev => ({
          ...prev,
          slug: error.response.data.msg || 'Este slug já está em uso.',
        }));
      } else {
        // Para outros erros (ex: falha de rede), logamos no console.
        console.error('Erro ao verificar slug:', error);
      }
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    let formattedValue = value; // Valor que será salvo no estado
    const currentErrors = { ...errors }; // Cópia dos erros atuais

    // --- LÓGICA DE VALIDAÇÃO EM TEMPO REAL ---

    // Validação de formato do Slug
    if (name === 'slug') {
      if (/[^a-z0-9-]/.test(value)) {
        currentErrors.slug = 'Use apenas letras minúsculas, números e hífens.';
      } else if (currentErrors.slug === 'Use apenas letras minúsculas, números e hífens.') {
        // Limpa o erro de formato, mas mantém o erro de duplicidade se houver
        delete currentErrors.slug;
      }
    }

    // Validação de CNPJ
    if (name === 'cnpjOrTaxId') {
      if (value && !validateBr.cnpj(value)) {
        currentErrors.cnpjOrTaxId = 'CNPJ inválido';
      } else {
        delete currentErrors.cnpjOrTaxId;
      }
    }

    // Validação de Inscrição Estadual (ao mudar o estado)
    if (name === 'state') {
      if (
        formData.stateRegistration &&
        !validateBr.inscricaoestadual(formData.stateRegistration, value)
      ) {
        currentErrors.ie = 'Inscrição Estadual inválida para o estado selecionado.';
      } else {
        delete currentErrors.ie;
      }
    }

    // Validação de CEP para busca automática
    // Removido: busca automática no onChange, agora no onBlur do campo

    // Formatação de campos (ex: apenas números)
    if (name === 'number') {
      formattedValue = value.replace(/\D/g, '');
    }

    // Atualiza o estado dos erros e do formulário
    setErrors(currentErrors);
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleStateRegistrationChange = e => {
    const { value } = e.target; // Este é o valor mascarado vindo do IMaskInput
    const currentErrors = { ...errors };

    // Valida o valor mascarado
    if (value && formData.state && !validateBr.inscricaoestadual(value, formData.state)) {
      currentErrors.ie = 'Inscrição Estadual inválida para o estado selecionado.';
    } else {
      delete currentErrors.ie;
    }

    setErrors(currentErrors);
    // Salva o valor mascarado no estado. O IMaskInput saberá como lidar com ele.
    setFormData(prev => ({ ...prev, stateRegistration: value }));
  };

  const handlePhoneChange = value => {
    setFormData(prev => ({
      ...prev,
      phone: value,
    }));
  };

  const handleAdminChange = (event, newValues) => {
    const newAdminOption = newValues.find(val => typeof val === 'string' || val.isNew);

    if (newAdminOption) {
      const newName = typeof newAdminOption === 'string' ? newAdminOption : newAdminOption.fullName;
      setNewAdminName(newName);
      // Mantém os admins já selecionados e adiciona o placeholder do novo
      const existingAdmins = newValues.filter(v => typeof v === 'object' && !v.isNew);
      setFormData(prev => ({ ...prev, adminIds: existingAdmins.map(a => a.id) }));
    } else {
      setNewAdminName('');
      setFormData(prev => ({ ...prev, adminIds: newValues.map(v => v.id) }));
    }
  };

  const handlePlanChange = newValue => {
    setFormData(prev => ({
      ...prev,
      subscribedPlanId: newValue ? newValue.id : null,
      // Atualiza os limites com base no plano selecionado, ou volta para o padrão se nenhum plano for selecionado
      trainersLimit: newValue ? newValue.trainersLimit : 10,
      studentsLimit: newValue ? newValue.studentsLimit : 100,
      modulesLimit: newValue ? newValue.modulesLimit : 20,
    }));
  };

  const handleLogoChange = e => {
    setFormData(prev => ({ ...prev, logo: e.target.files[0] }));
  };

  const handleAddressAutocompleteChange = (name, newValue) => {
    // Para Autocomplete de Cidade e País que usam o nome como valor
    setFormData(prev => ({
      ...prev,
      [name]: newValue ? newValue.name || newValue.nome : '',
    }));
  };

  const uploadLogo = async logoFile => {
    if (!logoFile) {
      return null;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('logo', logoFile);

      // Passar a URL antiga para o backend deletar
      if (company?.logoUrl) {
        formDataUpload.append('oldLogoUrl', company.logoUrl);
      }

      const response = await api.post('/uploads/company-logo', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        return response.data.logoUrl;
      }
      return null;
    } catch (error) {
      console.error('Erro ao fazer upload da logo:', error);
      throw new Error(error.response?.data?.msg || 'Erro ao fazer upload da logo.');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      alert('Por favor, corrija os campos inválidos antes de salvar.');
      return;
    }

    try {
      let logoUrl = null;

      // Se há um novo logo, fazer upload primeiro
      if (formData.logo && formData.logo instanceof File) {
        logoUrl = await uploadLogo(formData.logo);
      }

      let dataToSave = {
        ...formData,
        cnpjOrTaxId: formData.cnpjOrTaxId?.replace(/[^\d]/g, '') || '', // Salva CNPJ sem máscara e com segurança
        postalCode: formData.postalCode?.replace(/[^\d]/g, '') || '', // Salva CEP sem máscara e com segurança
        newAdminName: isCreating ? newAdminName : '', // Só envia newAdminName na criação
      };

      // Se o upload de logo foi bem-sucedido, atualizar a URL no formData
      if (logoUrl) {
        dataToSave.logoUrl = logoUrl;
      }

      // Remove o arquivo do formData, pois já foi enviado
      delete dataToSave.logo;

      // Se o tipo for CLIENT, remove o plano, pois clientes não têm plano
      if (dataToSave.type === 'CLIENT') {
        delete dataToSave.subscribedPlanId;
      }

      // Para edição, só envia campos editáveis
      if (!isCreating) {
        const editableFields = [
          'name',
          'slug',
          'type',
          'cnpjOrTaxId',
          'stateRegistration',
          'description',
          'address',
          'number',
          'complement',
          'district',
          'city',
          'state',
          'postalCode',
          'country',
          'phone',
          'emailContact',
          'primaryColor',
          'secondaryColor',
          'trainersLimit',
          'studentsLimit',
          'modulesLimit',
          'logoUrl',
        ];
        dataToSave = Object.fromEntries(
          Object.entries(dataToSave).filter(([key]) => editableFields.includes(key))
        );
      } else {
        // Remove campos que não devem ser enviados na criação
        delete dataToSave.adminIds;
      }

      onSave(dataToSave);
    } catch (error) {
      alert(error.message || 'Erro ao salvar empresa.');
    }
  };

  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => setStep(prev => prev - 1);

  const steps = [
    { number: 1, title: 'Dados da Empresa' },
    { number: 2, title: 'Endereço' },
    { number: 3, title: 'Configurações e Limites' },
  ];

  if (!company && !isCreating) return null;

  return (
    <div className="edit-modal-overlay">
      <div className="edit-modal card" onClick={e => e.stopPropagation()}>
        <div className="card-body">
          <div className="modal-header">
            <div className="modal-title">
              <h3>{isCreating ? 'Criar Nova Empresa' : 'Editar Empresa'}</h3>
              <p className="modal-sub">
                {isCreating ? 'Preencha os dados para registrar uma nova empresa.' : formData.slug}
              </p>
            </div>
            <button onClick={onClose} className="btn btn-secondary">
              ✕
            </button>
          </div>

          {loading ? (
            <div className="loading-state">Carregando...</div>
          ) : (
            <>
              <div className="step-indicator">
                {steps.map(s => {
                  const isCompleted = s.number < step;
                  const isActive = s.number === step;
                  const stepClass = `step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`;
                  return (
                    <div key={s.number} className={stepClass}>
                      <div className="step-number">{s.number}</div>
                      {s.title}
                    </div>
                  );
                })}
              </div>

              {/* O formulário agora envolve apenas o conteúdo das etapas para melhor controle de eventos */}
              <form id="company-edit-form" onSubmit={handleSubmit} className="modal-form">
                <div
                  onKeyDown={e => {
                    // Aciona o salvamento com "Enter" apenas na etapa 3
                    // e se o foco não estiver em um Autocomplete (para evitar salvar ao selecionar uma opção).
                    if (
                      step === 3 &&
                      e.key === 'Enter' &&
                      !e.target.closest('.MuiAutocomplete-root')
                    ) {
                      e.preventDefault(); // Previne qualquer comportamento padrão do Enter
                      handleSubmit(e);
                    }
                  }}
                >
                  {step === 1 && (
                    <CompanyStep1Data
                      formData={formData}
                      handleInputChange={handleInputChange}
                      handleStateRegistrationChange={handleStateRegistrationChange}
                      handlePhoneChange={handlePhoneChange}
                      handleUsernameBlur={handleUsernameBlur}
                      errors={errors}
                      PhoneInput={PhoneInput}
                      PhoneInputAdapter={PhoneInputAdapter}
                      brazilianStates={brazilianStates}
                      currentUser={currentUser}
                    />
                  )}

                  {step === 2 && (
                    <CompanyStep2Address
                      formData={formData}
                      handleInputChange={handleInputChange}
                      cities={cities}
                      loadingCities={loadingCities}
                      handleAddressAutocompleteChange={handleAddressAutocompleteChange}
                      countries={countries}
                    />
                  )}

                  {step === 3 && (
                    <CompanyStep3Settings
                      formData={formData}
                      handleInputChange={handleInputChange}
                      plans={plans}
                      admins={admins}
                      newAdminName={newAdminName}
                      adminInputValue={adminInputValue}
                      setAdminInputValue={setAdminInputValue}
                      handlePlanChange={handlePlanChange}
                      handleAdminChange={handleAdminChange}
                      handleLogoChange={handleLogoChange}
                      errors={errors}
                      currentUser={currentUser}
                      currentLogoUrl={buildLogoUrl(company?.logoUrl)}
                    />
                  )}
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={onClose} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <div
                    style={{
                      flexGrow: 1,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 'var(--space-3)',
                    }}
                  >
                    {step > 1 && (
                      <button type="button" onClick={handleBack} className="btn btn-secondary">
                        Anterior
                      </button>
                    )}
                    {step < 3 ? (
                      <div type="button" onClick={handleNext} className="btn btn-primary">
                        Próximo
                      </div>
                    ) : (
                      <button type="submit" form="company-edit-form" className="btn btn-primary">
                        Salvar
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
